import { useCallback, useEffect, useState } from 'react'
import {
  addExerciseToday,
  deleteSet,
  fetchExerciseCatalog,
  fetchWorkoutToday,
  removeExerciseToday,
  saveSession,
  saveSet,
  type CatalogExercise,
  type Exercise,
  type WorkoutToday,
} from '../lib/api'
import { Button, ErrorBanner, Group, LoadError, Screen, Spinner } from '../components/ui'
import { IconCheck, IconMinus, IconPlus, IconTrash, IconX } from '../components/icons'
import { ActivityDay } from './ActivityDay'

/**
 * Русская клавиатура даёт запятую, а Number('12,5') — это NaN. Раньше такой
 * подход сохранялся пустым: вес и повторы просто исчезали.
 */
function parseNumber(input: string): number | null {
  const normalized = input.replace(',', '.').trim()
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export function WorkoutScreen() {
  const [data, setData] = useState<WorkoutToday | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  // В беговой или прогулочный день зал не показываем, но дверь оставляем
  // открытой: сходить в зал сверх плана — нормально.
  const [gymAnyway, setGymAnyway] = useState(false)

  const reload = useCallback(async () => {
    try {
      setData(await fetchWorkoutToday())
      setLoadError(null)
    } catch (cause) {
      setLoadError((cause as Error).message)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  if (loadError && !data) {
    return <LoadError message={loadError} onRetry={() => void reload()} />
  }
  if (!data) return <Spinner label="Загружаю программу…" />

  if (data.focus !== 'gym' && !gymAnyway) {
    return <ActivityDay data={data} onChanged={reload} onOpenGym={() => setGymAnyway(true)} />
  }

  const pairs = [...new Set(data.exercises.map((exercise) => exercise.pair_no))].sort(
    (a, b) => a - b,
  )

  async function ensureSession(): Promise<string> {
    if (data?.session_id) return data.session_id
    const { sessionId } = await saveSession({
      kind: data?.gym_day === 'A' ? 'gym_a' : 'gym_b',
      completed: false,
    })
    await reload()
    return sessionId
  }

  const doneSets = data.exercises.reduce((sum, exercise) => sum + exercise.today_sets.length, 0)
  const planSets = data.exercises.reduce((sum, exercise) => sum + exercise.target_sets, 0)

  const share = planSets > 0 ? Math.min(1, doneSets / planSets) : 0

  return (
    <Screen title={`Зал ${data.gym_day}`}>
      {/* Прогресс тренировки кольцом-полосой того же зелёного, что и кольцо
          плана на главном экране: один цвет — одно значение во всём приложении. */}
      <div className="-mt-4">
        <div className="numerals flex items-baseline gap-2">
          <span className="text-[34px] font-semibold leading-none">{doneSets}</span>
          <span className="text-[17px] text-ink-2">из {planSets} подходов</span>
        </div>
        <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-ink-fill">
          <div
            className="h-full rounded-full bg-exercise transition-[width] duration-500"
            style={{ width: `${share * 100}%` }}
          />
        </div>
        <p className="footnote mt-2">Пары идут без отдыха внутри, 60 секунд между парами.</p>
      </div>

      {actionError && <ErrorBanner message={actionError} onClose={() => setActionError(null)} />}

      {pairs.map((pairNo) => {
        const inPair = data.exercises.filter((exercise) => exercise.pair_no === pairNo)
        const zone = inPair[0]?.zone
        const isExtra = inPair.every((exercise) => exercise.added_manually)
        return (
          <Group
            key={pairNo}
            caption={isExtra ? 'Добавлено сегодня' : `Пара ${pairNo}${zone ? ` · ${zone}` : ''}`}
          >
            {inPair.map((exercise) => (
              <ExerciseBlock
                key={exercise.id}
                exercise={exercise}
                sessionId={data.session_id}
                ensureSession={ensureSession}
                onChanged={reload}
                onError={setActionError}
              />
            ))}
          </Group>
        )
      })}

      {adding ? (
        <AddExercise
          gymDay={data.gym_day}
          onClose={() => setAdding(false)}
          onAdded={async () => {
            setAdding(false)
            await reload()
          }}
          onError={setActionError}
        />
      ) : (
        <Button full onClick={() => setAdding(true)}>
          <IconPlus size={17} />
          Своё упражнение
        </Button>
      )}

      <Group caption="После тренировки" footnote="Боль 3 и выше две недели подряд — снижаем нагрузку и идём к врачу.">
        <div className="px-4 py-3.5">
          <BackPainPicker
            value={data.back_pain_0_10}
            onPick={async (value) => {
              try {
                await saveSession({
                  kind: data.gym_day === 'A' ? 'gym_a' : 'gym_b',
                  completed: data.completed,
                  back_pain_0_10: value,
                })
                await reload()
              } catch (cause) {
                setActionError(`Не удалось сохранить оценку: ${(cause as Error).message}`)
              }
            }}
          />
        </div>
      </Group>

      {/* Раньше тренировка засчитывалась только вместе с оценкой спины —
          и без неё приложение считало, что занятия не было вовсе. */}
      <Button
        full
        variant={data.completed ? 'tinted' : 'filled'}
        color={data.completed ? 'var(--color-sys-blue)' : 'var(--color-exercise)'}
        onClick={async () => {
          try {
            await saveSession({
              kind: data.gym_day === 'A' ? 'gym_a' : 'gym_b',
              completed: !data.completed,
            })
            await reload()
          } catch (cause) {
            setActionError(`Не удалось отметить тренировку: ${(cause as Error).message}`)
          }
        }}
      >
        {data.completed ? 'Тренировка засчитана · отменить' : 'Завершить тренировку'}
      </Button>
    </Screen>
  )
}

function ExerciseBlock({
  exercise,
  sessionId,
  ensureSession,
  onChanged,
  onError,
}: {
  exercise: Exercise
  sessionId: string | null
  ensureSession: () => Promise<string>
  onChanged: () => Promise<void>
  onError: (message: string) => void
}) {
  const [weight, setWeight] = useState('')
  const [reps, setReps] = useState('')
  const [saving, setSaving] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<number | null>(null)

  const lastByNo = new Map(exercise.last_sets.map((set) => [set.set_no, set]))
  const nextNo = exercise.today_sets.length + 1
  const previous = lastByNo.get(nextNo) ?? exercise.last_sets[exercise.last_sets.length - 1] ?? null
  const previousValue = exercise.unit === 'seconds' ? previous?.seconds : previous?.reps

  const step = Number(exercise.step_kg) || 2.5
  const suggestedWeight = exercise.suggested_weight_kg ?? previous?.weight_kg ?? null

  async function addSet() {
    setSaving(true)
    try {
      const session = await ensureSession()
      // Пустые поля означают «как в прошлый раз»: подсказка стоит серым прямо
      // в поле, и повтор прошлой тренировки не требует ввода вообще.
      const weightValue = parseNumber(weight) ?? suggestedWeight
      const repsValue = parseNumber(reps) ?? previousValue ?? exercise.target_reps

      await saveSet({
        sessionId: session,
        exerciseId: exercise.id,
        setNo: nextNo,
        weightKg: weightValue,
        ...(exercise.unit === 'seconds' ? { seconds: repsValue } : { reps: repsValue }),
      })
      setWeight('')
      setReps('')
      await onChanged()
    } catch (cause) {
      onError(`Подход не записался: ${(cause as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  async function removeSet(setNo: number) {
    if (!sessionId) return
    try {
      await deleteSet({ sessionId, exerciseId: exercise.id, setNo })
      await onChanged()
    } catch (cause) {
      onError(`Не удалось удалить подход: ${(cause as Error).message}`)
    }
  }

  const field =
    'numerals min-h-12 rounded-[10px] bg-ink-surface2 px-2 text-center text-[19px] placeholder:text-ink-2'

  return (
    <div className="px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[17px]">{exercise.name}</span>
          <span className="numerals ml-2 text-[15px] text-ink-2">
            {exercise.today_sets.length}/{exercise.target_sets} × {exercise.target_reps}
            {exercise.unit === 'seconds' ? ' сек' : ''}
          </span>
        </div>
        {exercise.added_manually && (
          <button
            type="button"
            aria-label="Убрать упражнение"
            className="-mr-2 shrink-0 p-2 text-ink-3"
            onClick={async () => {
              try {
                await removeExerciseToday(exercise.id)
                await onChanged()
              } catch (cause) {
                onError(`Не удалось убрать упражнение: ${(cause as Error).message}`)
              }
            }}
          >
            <IconTrash size={17} />
          </button>
        )}
      </div>

      {exercise.suggested_weight_kg && (
        <p className="footnote mt-1 text-exercise">
          Закрыл все подходы в прошлый раз — можно {exercise.suggested_weight_kg} кг
        </p>
      )}
      {exercise.note && <p className="footnote mt-0.5 text-sys-orange">{exercise.note}</p>}

      {/* Записанные подходы: тап убирает подход. Раньше ошибочная запись
          оставалась в дневнике навсегда. */}
      {exercise.today_sets.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {exercise.today_sets.map((set) => (
            <button
              key={set.set_no}
              type="button"
              onClick={() => {
                if (pendingDelete === set.set_no) {
                  setPendingDelete(null)
                  void removeSet(set.set_no)
                } else {
                  setPendingDelete(set.set_no)
                }
              }}
              aria-label={
                pendingDelete === set.set_no
                  ? `Подтвердить удаление подхода ${set.set_no}`
                  : `Подход ${set.set_no}, нажми чтобы удалить`
              }
              className={`numerals flex min-h-11 items-center gap-1.5 rounded-full px-3.5 text-[15px] active:opacity-60 ${
                pendingDelete === set.set_no
                  ? 'bg-sys-red text-black'
                  : 'bg-exercise/15 text-exercise'
              }`}
            >
              {pendingDelete === set.set_no ? (
                <>
                  Удалить
                  <IconTrash size={14} />
                </>
              ) : (
                <>
                  {set.weight_kg ?? '—'}×{exercise.unit === 'seconds' ? set.seconds : set.reps}
                  <IconX size={13} />
                </>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Две строки, а не одна: пять элементов в ряд не помещаются в ширину
          телефона, а кнопка записи должна быть широкой — в неё целятся потным
          пальцем между подходами. */}
      <div className="mt-2.5 flex items-center gap-1.5">
        <button
          type="button"
          aria-label="Меньше вес"
          className="flex min-h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-ink-surface2 text-ink-2 active:opacity-60"
          onClick={() =>
            setWeight(String(Math.max(0, (parseNumber(weight) ?? suggestedWeight ?? 0) - step)))
          }
        >
          <IconMinus size={18} />
        </button>
        <label className="relative flex-1">
          <input
            inputMode="decimal"
            value={weight}
            onChange={(event) => setWeight(event.target.value)}
            placeholder={suggestedWeight !== null ? String(suggestedWeight) : '—'}
            aria-label="Вес в килограммах"
            className={`${field} w-full pr-8`}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-ink-2">
            кг
          </span>
        </label>
        <button
          type="button"
          aria-label="Больше вес"
          className="flex min-h-12 w-12 shrink-0 items-center justify-center rounded-[10px] bg-ink-surface2 text-ink-2 active:opacity-60"
          onClick={() => setWeight(String((parseNumber(weight) ?? suggestedWeight ?? 0) + step))}
        >
          <IconPlus size={18} />
        </button>
        <label className="relative flex-1">
          <input
            inputMode="numeric"
            value={reps}
            onChange={(event) => setReps(event.target.value)}
            placeholder={String(previousValue ?? exercise.target_reps)}
            aria-label={exercise.unit === 'seconds' ? 'Секунды' : 'Повторы'}
            className={`${field} w-full pr-9`}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-ink-2">
            {exercise.unit === 'seconds' ? 'сек' : 'повт'}
          </span>
        </label>
      </div>

      <button
        type="button"
        onClick={addSet}
        disabled={saving}
        className="mt-1.5 flex min-h-12 w-full items-center justify-center gap-1.5 rounded-[10px] bg-exercise text-[17px] font-semibold text-black disabled:opacity-30 active:opacity-70"
      >
        <IconCheck size={18} />
        {saving ? 'Пишу…' : `Записать подход ${nextNo}`}
      </button>

      {previous && (
        <p className="footnote mt-2">
          Прошлый раз: {previous.weight_kg ?? '—'} кг ×{' '}
          {exercise.last_sets
            .map((set) => (exercise.unit === 'seconds' ? set.seconds : set.reps))
            .join('/')}
        </p>
      )}
    </div>
  )
}

/** Добавление упражнения: из справочника или своё, разово или в программу. */
function AddExercise({
  gymDay,
  onClose,
  onAdded,
  onError,
}: {
  gymDay: 'A' | 'B'
  onClose: () => void
  onAdded: () => Promise<void>
  onError: (message: string) => void
}) {
  const [catalog, setCatalog] = useState<CatalogExercise[] | null>(null)
  const [name, setName] = useState('')
  const [sets, setSets] = useState('3')
  const [reps, setReps] = useState('12')
  const [forever, setForever] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void fetchExerciseCatalog()
      .then((data) => setCatalog(data.exercises))
      .catch(() => setCatalog([]))
  }, [])

  async function add(payload: Parameters<typeof addExerciseToday>[0]) {
    setBusy(true)
    try {
      await addExerciseToday(payload)
      await onAdded()
    } catch (cause) {
      onError(`Не удалось добавить: ${(cause as Error).message}`)
      setBusy(false)
    }
  }

  return (
    <Group caption="Добавить упражнение">
      <div className="flex flex-col gap-2.5 px-4 py-3.5">
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Название, например «Гиперэкстензия»"
          className="numerals min-h-12 rounded-[10px] bg-ink-surface2 px-3 text-[17px] placeholder:text-ink-2 text-left"
        />
        <div className="flex items-center gap-2 text-[17px]">
          <span className="text-ink-2">Подходы</span>
          <input
            inputMode="numeric"
            value={sets}
            onChange={(event) => setSets(event.target.value)}
            aria-label="Число подходов"
            className="numerals min-h-12 rounded-[10px] bg-ink-surface2 px-3 text-[17px] placeholder:text-ink-2 w-16 text-center"
          />
          <span className="text-ink-2">по</span>
          <input
            inputMode="numeric"
            value={reps}
            onChange={(event) => setReps(event.target.value)}
            aria-label="Число повторов"
            className="numerals min-h-12 rounded-[10px] bg-ink-surface2 px-3 text-[17px] placeholder:text-ink-2 w-16 text-center"
          />
          <span className="text-ink-2">повторов</span>
        </div>

        <label className="flex min-h-11 items-center gap-2.5 text-[17px]">
          <input
            type="checkbox"
            checked={forever}
            onChange={(event) => setForever(event.target.checked)}
            className="size-5 accent-[var(--color-exercise)]"
          />
          Оставить в программе Зала {gymDay} насовсем
        </label>

        <div className="flex gap-2">
          <Button
            variant="filled"
            color="var(--color-exercise)"
            disabled={busy || !name.trim()}
            onClick={() =>
              add({
                name: name.trim(),
                targetSets: Number(sets) || 3,
                targetReps: Number(reps) || 12,
                scope: forever ? gymDay : 'today',
              })
            }
          >
            Добавить
          </Button>
          <Button onClick={onClose} disabled={busy}>
            Отмена
          </Button>
        </div>
      </div>

      {catalog && catalog.length > 0 && (
        <div className="border-t border-ink-separator px-4 py-3.5">
          <p className="footnote mb-2">Или из уже знакомых</p>
          <div className="flex flex-wrap gap-1.5">
            {catalog.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={busy}
                onClick={() => void add({ exerciseId: item.id })}
                className="min-h-9 rounded-full bg-ink-surface2 px-3 text-[15px] disabled:opacity-30 active:opacity-60"
              >
                {item.name}
                {item.gym_day !== 'custom' && (
                  <span className="ml-1 text-ink-3">{item.gym_day}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </Group>
  )
}

function BackPainPicker({
  value,
  onPick,
}: {
  value: number | null
  onPick: (value: number) => Promise<void>
}) {
  return (
    <div>
      <p className="mb-1 text-[17px]">Спина сегодня</p>
      <p className="footnote mb-3">0 — не беспокоила, 10 — сильная боль</p>

      {/* Шкала окрашивается от зелёного к красному: цвет — второй сигнал
          рядом с числом, а не единственный. */}
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 11 }, (_, index) => {
          const active = value === index
          const tone =
            index <= 2 ? 'var(--color-sys-green)' : index <= 5 ? 'var(--color-sys-orange)' : 'var(--color-sys-red)'
          return (
            <button
              key={index}
              type="button"
              onClick={() => void onPick(index)}
              aria-pressed={active}
              className="numerals size-11 rounded-full text-[17px] active:opacity-60"
              style={
                active
                  ? { background: tone, color: '#000', fontWeight: 600 }
                  : { background: 'var(--color-ink-surface2)', color: 'var(--color-ink-2)' }
              }
            >
              {index}
            </button>
          )
        })}
      </div>
    </div>
  )
}
